import React from 'react';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { get } from 'lodash';
import { ProductListAttributes } from '../../models';
import ResourceTable, { IColumn } from '../../components/ResourceTable';
import { GetProductLists, CreateProductList } from 'services/productList';
import CreateResourceModal, { ISchema } from '../../dialogs/CreateResourceModal';
import Notice from '../../components/Notice';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import generateNonce from 'utils/generateNonce';
import Link from 'components/Link';
import CurrentUser from 'auth/currentUser';
import { TFunction, useTranslation } from 'react-i18next';
import { ProductListDto } from 'models/dtoTypes';

const PREFIX = 'ProductListsPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',

    '& > button': {
      position: 'absolute',
      right: 0,
      top: 0,
    },
  },
}));

const getColumns = (t: TFunction, isAdmin?: boolean) => {
  const columns: IColumn<ProductListDto>[] = [
    {
      label: t('name'),
      sortIndex: 'name',
      render: (model: ProductListDto) => {
        console.log(`/productLists/${model?.id ?? ''}`);
        return <Link to={`/productLists/${model?.id ?? ''}`} text={model?.name ?? ''} />;
      },
    },
    {
      label: t('description'),
      dataIndex: 'description',
    },
    ...(isAdmin
      ? [
          {
            label: t('organisation'),
            render: (model: ProductListDto) => {
              const orgName = model.organisationName || '(unknown)';
              const orgId = model?.organisationId as string;
              if (orgName) {
                return <Link to={`/organisations/${orgId || ''}`} text={orgName} />;
              } else {
                return <></>;
              }
            },
          },
        ]
      : []),
    // {
    //   label: t('Verified / Total'),
    //   dataIndex: 'companyCount',
    //   render: (model: ProductListDto) => {
    //     return `${model.companyVerifiedCount ?? 0} / ${model.companyCount ?? 0}`;
    //   },
    // },
  ];
  return columns;
};

const getSchema = (t: TFunction, isAdmin?: boolean) => {
  const schema: ISchema = {
    fields: [
      {
        label: t('list-name'),
        name: 'name',
        required: true,
        widget: 'text',
        description: '',
      },
      {
        label: t('description'),
        name: 'description',
        required: true,
        widget: 'text',
      },
    ],
  };

  if (isAdmin) {
    schema.fields.push({
      label: t('organisation'),
      name: 'organisationId',
      required: true,
      widget: 'organisationSelector',
    });
  }
  return schema;
};

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

export default function ProductListsPage({ isAdmin }: { isAdmin?: boolean }): JSX.Element {
  const [openCreate, setOpenCreate] = React.useState(false);
  const [nonce, setNonce] = React.useState('');
  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const organisationId = CurrentUser.getInstance().getOrganisationId();
  const { t } = useTranslation('default');

  const handleError = (e: unknown) => {
    if (axios.isAxiosError(e) && e.response) {
      console.log(e.response?.data);
      const errorMessage = get(e.response?.data, 'message') || t('something-went-wrong');
      setNoticeData({
        message: errorMessage,
        severity: 'error',
      });
    } else {
      // native error
      console.error(e);
      setNoticeData({
        message: e instanceof Error ? e.message : String(e),
        severity: 'error',
      });
    }
  };

  const handleCloseNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNoticeData(undefined);
  };

  const handleCreate = async (attrs: ProductListAttributes) => {
    try {
      await CreateProductList(attrs);
      setNonce(generateNonce);
      setOpenCreate(false);
      setNoticeData({
        message: t('successfully-created'),
        severity: 'success',
      });
    } catch (e) {
      handleError(e);
    }
  };

  const filterSchema = [
    {
      label: t('name-contains'),
      name: 'name',
      widget: 'text',
    },
  ];

  return (
    <Root>
      <div className={classes.pageHeader}>
        <h2>{t('Product Lists')}</h2>
        <Button onClick={() => setOpenCreate(true)} variant="contained" startIcon={<AddIcon />}>
          {t('create')}
        </Button>
      </div>
      <ResourceTable
        filterSchema={filterSchema}
        key={nonce}
        columns={getColumns(t, isAdmin)}
        onFetch={GetProductLists}
        defaultQuery={
          !isAdmin
            ? [
                {
                  column: 'organisationId',
                  value: organisationId,
                },
              ]
            : undefined
        }
        defaultSort={{
          sortColumn: 'created',
          isDesc: true,
        }}
      />
      <CreateResourceModal<ProductListAttributes>
        open={openCreate}
        label={t('Create a new product list')}
        schema={getSchema(t, isAdmin)}
        onClose={() => setOpenCreate(false)}
        onSave={handleCreate}
      />
      {noticeData && (
        <Notice
          open={true}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseNotice}
        />
      )}
    </Root>
  );
}
